import kebabCase from 'lodash.kebabcase'
import { getGtmClient } from 'marketing-site/lib/gtm'
import { IntercomContext } from 'marketing-site/src/components/context/IntercomContext'
import {
  AnalyticsEvent,
  googleTagManagerCustomEvents,
  triggerGoogleTagManagerCustomEvent,
} from 'marketing-site/src/library/utils'
import { useContext, useState } from 'react'

interface IClickHandlerProps {
  anchorRef: React.RefObject<HTMLAnchorElement>
  label: string
  onClick?: () => void
  openMessenger?: boolean
  preloadedMessengerText?: string
  dataAttribute?: string
  surveyId?: number
  customBotId?: number
  eventContext?: string
  eventObjectName?: string
}

interface IClickHandler {
  handleClick: (event: React.MouseEvent<HTMLAnchorElement>) => void
  isLoading: boolean
}

interface ITriggerResponse {
  success: boolean
}

export const CUSTOM_BOT_RETRY_TIMEOUT = 2500
const MAX_API_BOT_TRIGGER_RETRIES = 1
const TRIGGER_RETRY_DELAY_MS = 2000
const baseMetricName = 'BotsOnButtons.'
const metricNames = {
  triggered: 'triggered',
  failed: 'failed',
  successful: 'successful',
  retryTriggered: 'retryTriggered',
  retrySuccessful: 'retrySuccessful',
}

export default function useClickHandler({
  anchorRef,
  label,
  onClick,
  openMessenger,
  preloadedMessengerText,
  dataAttribute,
  surveyId,
  customBotId,
  eventContext,
  eventObjectName,
}: IClickHandlerProps): IClickHandler {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { isMessengerOpen } = useContext(IntercomContext)

  const isCustomBotFromAttribute = !!dataAttribute && !openMessenger && !surveyId && !customBotId
  const isCustomBotFromApi = !!customBotId

  const [t0, setT0] = useState<number>(0)

  async function triggerAndRetry(
    trigger: () => Promise<ITriggerResponse | undefined>,
    numberOfRetries = 0,
  ) {
    setIsLoading(true)

    const gtmClient = await getGtmClient()
    gtmClient.incrementMetric(
      `${baseMetricName}${
        numberOfRetries === 0 ? metricNames.triggered : metricNames.retryTriggered
      }`,
    )

    const response = await trigger()

    if (!response?.success) {
      if (numberOfRetries < MAX_API_BOT_TRIGGER_RETRIES) {
        setTimeout(() => triggerAndRetry(trigger, numberOfRetries + 1), TRIGGER_RETRY_DELAY_MS)
        return
      }

      // no more retries, trigger failed
      window.Intercom('show')
      gtmClient.incrementMetric(`${baseMetricName}${metricNames.failed}`)
    } else {
      gtmClient.incrementMetric(
        `${baseMetricName}${
          numberOfRetries === 0 ? metricNames.successful : metricNames.retrySuccessful
        }`,
      )
    }

    setIsLoading(false)
  }

  const handleClick: IClickHandler['handleClick'] = (event) => {
    // Prevent duplicated clicks and events if we're retrying
    if (!isLoading) {
      onClick?.()
      fireAnalyticsEvents()
    }

    if (isCustomBotFromAttribute) {
      forceMessengerOpenIfNeeded(() => anchorRef?.current?.click())
    } else if (!!surveyId) {
      window.Intercom('startSurvey', surveyId)
    } else if (isCustomBotFromApi) {
      triggerAndRetry(() => window.Intercom('trigger', customBotId))
    }

    if (!event.currentTarget || !event.currentTarget.href) {
      return
    }

    // Custom bots will only fire off the first matching element on a page. This is an internal API from team-operator to work around this
    if (eventObjectName && eventObjectName.includes('talk_to_sales_test_cta')) {
      window.Intercom('trigger', '12768')
    }

    if (openMessenger) {
      window.Intercom('showNewMessage', preloadedMessengerText)
    }
  }

  const fireAnalyticsEvents = () => {
    const context = eventContext || kebabCase(label)
    const data = {
      action: 'clicked',
      object: eventObjectName || 'cta_link',
      context,
    }

    const analyticsEvent = new AnalyticsEvent(data)
    analyticsEvent.setPlaceFromPath(window.location.pathname)
    analyticsEvent.send()

    const metadata = { context }
    triggerGoogleTagManagerCustomEvent(googleTagManagerCustomEvents.ctaLinkClicked, metadata)
  }

  const forceMessengerOpenIfNeeded = (retry: () => void) => {
    if (!isLoading) {
      startTimer()
    }

    if (isMessengerOpen) {
      // If the messenger is open, that means the bot already fired
      setIsLoading(false)
      recordBotTriggerTiming()
    } else {
      // If the messenger isn't open, the bot didn't fire yet
      // We disable analytics (to prevent duplicates) and trigger a new click on the button
      setIsLoading(true)

      setTimeout(() => {
        if (!isMessengerOpen) retry()
      }, CUSTOM_BOT_RETRY_TIMEOUT)
    }
  }

  const startTimer = () => {
    setT0(window.performance.now())
  }

  const recordBotTriggerTiming = async () => {
    const gtmClient = await getGtmClient()
    const elapsed = window.performance.now() - t0
    const tags = { path: window.location.pathname }
    gtmClient.recordTimingMetric('MarketingSite.CTALink.CustomBotTrigger', elapsed, tags)
  }

  return { handleClick, isLoading }
}
